import { useState } from 'react'

import { cameraLocationLabel } from './cameras-screen'
import type { Camera } from './camera-types'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useCameras } from '@/hooks/use-cameras-query'
import { m } from '@/lib/paraglide/messages'

interface Props {
    open: boolean
    /** Camera đã ở nơi đang xem thì không hiện trong danh sách chọn. */
    isHere: (camera: Camera) => boolean
    /** Câu giải thích dưới tiêu đề (vùng: chuyển camera; trạm: thêm vào). */
    description: string
    /** Nhãn nút xác nhận ("Gắn vào vùng này" / "Gắn vào trạm này"). */
    submitLabel: string
    isSaving: boolean
    onAttach: (cameraId: string) => Promise<unknown>
    onClose: () => void
}

/**
 * Gắn một camera CÓ SẴN vào nơi đang xem (một vùng trồng hoặc một trạm bơm).
 * Mỗi dòng ghi nơi camera đang đặt để người dùng biết mình đang chuyển/thêm gì.
 */
export function AttachCameraDialog({
    open,
    isHere,
    description,
    submitLabel,
    isSaving,
    onAttach,
    onClose,
}: Props) {
    const { data: cameras = [] } = useCameras()
    const choices = cameras.filter((c) => !isHere(c))
    const [cameraId, setCameraId] = useState('')
    const [error, setError] = useState<string | null>(null)

    const close = () => {
        setCameraId('')
        setError(null)
        onClose()
    }

    const submit = async () => {
        if (!cameraId) {
            setError(m.camera_attach_required())
            return
        }
        try {
            await onAttach(cameraId)
            close()
        } catch {
            // Hook đã toast lỗi; giữ hộp thoại để người dùng thử lại.
        }
    }

    return (
        <Dialog open={open} onOpenChange={(next) => !next && close()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{m.camera_attach_title()}</DialogTitle>
                    <DialogDescription>{description}</DialogDescription>
                </DialogHeader>
                {choices.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                        {m.camera_attach_empty()}
                    </p>
                ) : (
                    <div className="space-y-1.5">
                        <Label htmlFor="attach-camera" required>
                            {m.camera_attach_field()}
                        </Label>
                        <Select
                            value={cameraId}
                            onValueChange={(v) => {
                                setCameraId(v)
                                setError(null)
                            }}
                        >
                            <SelectTrigger
                                id="attach-camera"
                                aria-label={m.camera_attach_field()}
                                className="w-full"
                            >
                                <SelectValue
                                    placeholder={m.camera_attach_placeholder()}
                                />
                            </SelectTrigger>
                            <SelectContent>
                                {choices.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>
                                        {c.name} — {cameraLocationLabel(c)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {error && (
                            <p className="mt-1 text-xs text-destructive">
                                {error}
                            </p>
                        )}
                    </div>
                )}
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={close}>
                        {m.common_cancel()}
                    </Button>
                    {choices.length > 0 && (
                        <Button
                            type="button"
                            disabled={isSaving}
                            onClick={() => void submit()}
                        >
                            {submitLabel}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
